import { watch, onBeforeUnmount, WatchStopHandle, Ref, isRef } from 'vue';
import { useStore } from 'vuex';
import { getSEOManager } from '@/plugins/seo';
import { SEOOptions } from '@/plugins/seo/types';

export interface UseSEOReturn {
    updateTitle: (title: string) => void;
    updateDescription: (description: string) => void;
    updateKeywords: (keywords: string | string[]) => void;
    updateMeta: (key: string, content: string, attributeType?: 'name' | 'property' | 'httpEquiv') => void;
    updateCurrentUrl: () => void;
    updatePublishTime: (publishTime: string | Date) => void;
    reset: () => void;
    stop: () => void;
}

export function useSEO(options: SEOOptions = {}): UseSEOReturn {
    let store;
    try {
        store = useStore();
    } catch (e) {
        // Store not available (e.g., in tests), proceed with SEO operations
        store = null;
    }

    const seoManager = getSEOManager();
    const watchers: WatchStopHandle[] = [];

    // Early return for native platform - no watchers or operations needed
    if (store && store.getters.isNative) {
        console.log('useSEO: Skipping SEO setup on native platform');
        return {
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            updateTitle: () => {},
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            updateDescription: () => {},
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            updateKeywords: () => {},
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            updateMeta: () => {},
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            updateCurrentUrl: () => {},
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            updatePublishTime: () => {},
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            reset: () => {},
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            stop: () => {},
        };
    }

    if (!seoManager) {
        console.warn('SEO Manager not found. Make sure SEOPlugin is installed.');
        return {
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            updateTitle: () => {},
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            updateDescription: () => {},
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            updateKeywords: () => {},
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            updateMeta: () => {},
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            updateCurrentUrl: () => {},
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            updatePublishTime: () => {},
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            reset: () => {},
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            stop: () => {},
        };
    }

    // Apply initial options
    seoManager.applyOptions(options);

    // Set up reactive watchers for ref values
    const setupWatchers = () => {
        // Watch title changes
        if (isRef(options.title)) {
            const titleWatcher = watch(
                options.title as Ref<string>,
                (newTitle) => {
                    seoManager.setTitle(newTitle);
                },
                { immediate: false }
            );
            watchers.push(titleWatcher);
        }

        // Watch description changes
        if (isRef(options.description)) {
            const descriptionWatcher = watch(
                options.description as Ref<string>,
                (newDescription) => {
                    seoManager.setMetaTag('description', newDescription);
                },
                { immediate: false }
            );
            watchers.push(descriptionWatcher);
        }

        // Watch keywords changes
        if (isRef(options.keywords)) {
            const keywordsWatcher = watch(
                options.keywords as Ref<string | string[]>,
                (newKeywords) => {
                    const keywordString = Array.isArray(newKeywords) ? newKeywords.join(', ') : newKeywords;
                    seoManager.setMetaTag('keywords', keywordString);
                },
                { immediate: false }
            );
            watchers.push(keywordsWatcher);
        }

        // Watch canonical changes
        if (isRef(options.canonical)) {
            const canonicalWatcher = watch(
                options.canonical as Ref<string>,
                (newCanonical) => {
                    seoManager.setCanonical(newCanonical);
                },
                { immediate: false }
            );
            watchers.push(canonicalWatcher);
        }

        // Watch jsonLd changes
        if (isRef(options.jsonLd)) {
            const jsonLdWatcher = watch(
                options.jsonLd as Ref<Record<string, any> | Record<string, any>[]>,
                (newJsonLd) => {
                    if (Array.isArray(newJsonLd)) {
                        newJsonLd.forEach((data, index) => {
                            seoManager.setStructuredData(data, `jsonld-${index}`);
                        });
                    } else {
                        seoManager.setStructuredData(newJsonLd, 'jsonld-0');
                    }
                },
                { immediate: false, deep: true }
            );
            watchers.push(jsonLdWatcher);
        }

        // Watch ogTitle changes
        if (isRef(options.ogTitle)) {
            const ogTitleWatcher = watch(
                options.ogTitle as Ref<string>,
                (newOgTitle) => {
                    seoManager.setMetaTag('og:title', newOgTitle, 'property');
                },
                { immediate: false }
            );
            watchers.push(ogTitleWatcher);
        }

        // Watch ogDescription changes
        if (isRef(options.ogDescription)) {
            const ogDescriptionWatcher = watch(
                options.ogDescription as Ref<string>,
                (newOgDescription) => {
                    seoManager.setMetaTag('og:description', newOgDescription, 'property');
                },
                { immediate: false }
            );
            watchers.push(ogDescriptionWatcher);
        }

        // Watch ogImage changes
        if (isRef(options.ogImage)) {
            const ogImageWatcher = watch(
                options.ogImage as Ref<string>,
                (newOgImage) => {
                    seoManager.setMetaTag('og:image', newOgImage, 'property');
                },
                { immediate: false }
            );
            watchers.push(ogImageWatcher);
        }
    };

    setupWatchers();

    // Manual update functions
    const updateTitle = (title: string): void => {
        seoManager.setTitle(title);
        // Also update OG title for consistency
        seoManager.setMetaTag('og:title', title, 'property');
    };

    const updateDescription = (description: string): void => {
        seoManager.setMetaTag('description', description);
        // Also update OG description for consistency
        seoManager.setMetaTag('og:description', description, 'property');
    };

    const updateKeywords = (keywords: string | string[]): void => {
        const keywordString = Array.isArray(keywords) ? keywords.join(', ') : keywords;
        seoManager.setMetaTag('keywords', keywordString);
    };

    const updateMeta = (key: string, content: string, attributeType: 'name' | 'property' | 'httpEquiv' = 'property'): void => {
        seoManager.setMetaTag(key, content, attributeType);
    };

    const updateCurrentUrl = (): void => {
        seoManager.updateCurrentPageUrl();
    };

    const updatePublishTime = (publishTime: string | Date): void => {
        const publishTimeString = publishTime instanceof Date ? publishTime.toISOString() : publishTime;
        seoManager.setMetaTag('og:published_time', publishTimeString, 'property');
    };

    const reset = (): void => {
        seoManager.reset();
    };

    const stop = (): void => {
        watchers.forEach((watcher) => watcher());
        watchers.splice(0);
    };

    // Cleanup watchers on component unmount (only if inside a component)
    try {
        onBeforeUnmount(() => {
            stop();
        });
    } catch (e) {
        // Not inside a component, manual cleanup will be needed
    }

    return {
        updateTitle,
        updateDescription,
        updateKeywords,
        updateMeta,
        updateCurrentUrl,
        updatePublishTime,
        reset,
        stop,
    };
}
